import {Fragment, useCallback, useMemo, useRef, useState} from 'react';
import Form from '../index';
import {createGalleryBatch, getGalleryCategoryId, isGalleryBatchReady} from './gallery-items';
import {getAcceptedFileTypes} from './uploader/uploader-utils';
import InputGroup from './input-group';
import Actions from '../layouts/actions';
import {Menu, Transition} from '@headlessui/react';
import ChevronDownIcon from '@heroicons/react/solid/ChevronDownIcon';

export default function GalleryUploadForm({initialFiles, category, categories = [], fileUploadSettings, onDone, onCancel}) {
    const [files, setFiles] = useState([]);
    const [details, setDetails] = useState({});
    const [defaultCategory, setDefaultCategory] = useState(() => getGalleryCategoryId(category) || '');
    const acceptedTypes = getAcceptedFileTypes({type: 'image-uploader'}).split(',');
    const acceptedFiles = useMemo(() => initialFiles.filter(file => acceptedTypes.includes(file.type)), [initialFiles]);
    const [rejected, setRejected] = useState(() => initialFiles.filter(file => !acceptedTypes.includes(file.type)));
    const completed = useRef(false);
    const onFilesChange = useCallback(next => setFiles([...next]), []);
    const onFileReplace = useCallback((previousId, nextId) => setDetails(previous => {
        if (!Object.prototype.hasOwnProperty.call(previous, previousId)) return previous;
        const next = {...previous, [nextId]: previous[previousId]};
        delete next[previousId];
        return next;
    }), []);
    const onUploadError = useCallback((error, file) => file.update({
        status: 'error', uploadError: true,
        error: error.response?.status === 413 ? 'File Too Large' : 'Could Not Upload'
    }), []);
    const options = useMemo(() => categories.map(item => ({label: item.title, value: item.id})), [categories]);
    const renderFileDetails = useCallback((file) => {
        const values = {title: '', category: defaultCategory, ...details[file.id]};
        const change = (name, value) => setDetails(previous => ({...previous, [file.id]: {
            title: '', category: defaultCategory, ...previous[file.id], [name]: value
        }}));
        return <>
                <InputGroup type="text" label="Caption" id={`caption-${file.id}`} name={`caption-${file.id}`}
                    value={values.title} placeholder="Add A Caption"
                    onFieldChange={(_event, _name, value) => change('title', value)}/>
                {options.length ? <InputGroup type="select" label="Category" optional={false}
                    id={`category-${file.id}`} name={`category-${file.id}`} options={options} placeholder="Unassigned"
                    value={values.category} onFieldChange={(_event, _name, value) => change('category', value || '')}/> : null}
        </>;
    }, [details, defaultCategory, options]);
    const ready = !rejected.length && isGalleryBatchReady(files);
    const failed = files.filter(file => file.status === 'error');
    const uploaded = files.filter(file => file.status === 'ready' && file.key).length;
    const fileListSummary = <p role="status" className="sr-only">{!files.length || uploaded === files.length
        ? ''
        : failed.length ? `${failed.length} ${failed.length === 1 ? 'Image Needs' : 'Images Need'} Attention`
        : `Uploading ${uploaded} Of ${files.length}…`}</p>;
    const fileListHeader = options.length || rejected.length ? <div className="space-y-3 mt-3">
        {options.length ? <Menu as="div" className="relative">
            <Menu.Button type="button" className="inline-flex items-center gap-1 h-8 text-sm font-medium text-gray-700 focus:outline-none focus-visible:ring-2 rounded">
                Set Category For All<ChevronDownIcon className="w-4 h-4"/>
            </Menu.Button>
            <Transition as={Fragment} enter="transition ease-out duration-100" enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100" leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100" leaveTo="transform opacity-0 scale-95">
                <Menu.Items className="origin-top-left absolute left-0 mt-1 w-48 z-20 menu-container focus:outline-none">
                    <div className="flex flex-col">{[{value: '', label: 'Unassigned'}, ...options].map(option =>
                        <Menu.Item as="button" type="button" key={option.value} className="grow block menu-item text-left"
                            onClick={() => {
                                setDetails(previous => Object.fromEntries(files.map(file => [file.id, {...previous[file.id], category: option.value}])));
                                setDefaultCategory(option.value);
                            }}>{option.label}</Menu.Item>
                    )}</div>
                </Menu.Items>
            </Transition>
        </Menu> : null}
        {rejected.map((file, index) => <div key={`${file.name}_${index}`} className="flex items-center justify-between gap-2 text-xs text-error-700" role="alert">
            <span>{file.name}: This file type is not supported.</span>
            <Actions header actions={[{label: 'Remove', color: 'error', link: true,
                onClick: () => setRejected(previous => previous.filter((_file, i) => i !== index))}]}/>
        </div>)}
    </div> : null;
    const fieldsets = useMemo(() => [{fields: [{
        type: 'image-uploader', name: 'images', multiple: true, required: true,
        initialFiles: acceptedFiles, onFilesChange, onFileReplace, onUploadError, unsplash: false,
        renderFileDetails, fileListHeader, fileListSummary,
        pintura: {objectFit: 'contain'},
    }]}], [acceptedFiles, onFilesChange, onFileReplace, onUploadError, renderFileDetails, fileListHeader, fileListSummary]);

    return <Form id="gallery_upload_form" layout="panel-style" heading={`Review Items${files.length ? ` (${files.length})` : ''}`} preserveValuesOnFieldsetChange
                 fileUploadSettings={fileUploadSettings} fieldsets={fieldsets}
                 actions={[{label: 'Cancel', back: true}, {label: 'Add Items', primary: true, disabled: !ready}]}
                 onActionClick={(_event, action) => { if (action.back) onCancel(); }}
                 onSubmit={() => {
                     if (completed.current || !ready) return;
                     completed.current = true;
                     onDone(createGalleryBatch(files, defaultCategory, details));
                 }}/>;
}
